import { useMemo, useState } from 'react';

import { Link, useNavigate, useSearchParams } from 'react-router';

import { ApiError } from '@mds/shared/lib/api';

import AuthShell, { AuthError, AuthNotice } from '@/components/auth/auth-shell';
import Button from '@/components/ui/button';
import Field, { inputCls } from '@/components/ui/field';
import * as auth from '@/auth/admin-auth.api';
import { PASSWORD_MIN_LENGTH, resetPasswordSchema } from '@/domain/administrators';
import { useValidation } from '@/lib/validation';

/** The shape of the token the API issues. Anything else was cut short or mangled in transit. */
const TOKEN = /^[A-Za-z0-9_-]{43}$/;

/**
 * Sets a new password from the emailed link.
 *
 * A successful reset revokes every existing session server-side, so this ends
 * at the login screen rather than signing anyone in.
 */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const draft = useMemo(() => ({ password, confirm }), [password, confirm]);
  const validation = useValidation(resetPasswordSchema, draft);

  const submit = async () => {
    if (!validation.check()) return;

    setBusy(true);
    setError(null);
    try {
      await auth.resetPassword(token, password);
      setDone(true);
      // Long enough to read the confirmation, short enough not to strand them.
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not reset the password. Try again.');
      setBusy(false);
    }
  };

  if (!TOKEN.test(token)) {
    return (
      <AuthShell
        title="That link is incomplete"
        hint="Reset links carry a token. Ask for a new one and use the most recent email."
        footer={
          <Link
            to="/forgot-password"
            className="text-ink-muted underline underline-offset-4 hover:text-brand-text"
          >
            Request another link
          </Link>
        }
      >
        <AuthError>
          {token
            ? 'The reset token in the address is damaged. It may have been cut short when copied.'
            : 'No reset token was found in the address.'}
        </AuthError>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Set a new password"
      hint="This signs you out everywhere else."
      footer={
        <Link
          to="/login"
          className="text-ink-muted underline underline-offset-4 hover:text-brand-text"
        >
          Back to sign in
        </Link>
      }
    >
      {done ? (
        <AuthNotice>Password changed. Taking you to the sign-in screen…</AuthNotice>
      ) : (
        <form
          className="flex flex-col gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Field
            label="New password"
            required
            hint={`At least ${PASSWORD_MIN_LENGTH} characters.`}
            {...validation.field('password')}
          >
            <input
              type="password"
              className={inputCls}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              autoFocus
            />
          </Field>

          <Field label="New password again" required {...validation.field('confirm')}>
            <input
              type="password"
              className={inputCls}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </Field>

          {error && <AuthError>{error}</AuthError>}

          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? 'Saving…' : 'Save the new password'}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
